import { type IconCommonV2Props, IconV2, ReadOnlyTag, Tooltip } from '@abtasty/design-system';
import { TestStatus, TestStatusCategory, TestStatusForced } from '../../../shared/tag/serializedTagTypes';
import { getTestStatusCategory } from '../../../shared/tag/helpers';
import { useDesktopStatusReason } from '../../hooks/useStatusReason';
import { TagVariant } from '@abtasty/design-system/dist/components/TagV2/types';
import { styled, useTheme } from 'styled-components';

type CampaignStatusProps = {
  status: TestStatus;
  noTooltip?: boolean;
};

const variantByCategory: Record<TestStatusCategory, TagVariant> = {
  [TestStatusCategory.rejected]: 'negative',
  [TestStatusCategory.accepted]: 'positive',
  [TestStatusCategory.waiting]: 'tone',
  [TestStatusCategory.forced]: 'moderate',
};

const ProxyTooltip: typeof Tooltip = ({ children, className, ...props }) => {
  return (
    <Tooltip {...props} targetClassName={className}>
      {children}
    </Tooltip>
  );
};

const FullWidthTooltip = styled(ProxyTooltip)`
  max-width: 100%;
`;

export function CampaignStatusTag({ status, noTooltip }: CampaignStatusProps) {
  const category = getTestStatusCategory(status);
  const reason = useDesktopStatusReason(status);
  const variant = variantByCategory[category];
  const tooltip = noTooltip ? undefined : reason;

  return (
    <FullWidthTooltip content={tooltip} placement="bottom">
      <ReadOnlyTag variant={variant} size="medium">
        {reason}
      </ReadOnlyTag>
    </FullWidthTooltip>
  );
}

export function CampaignStatusIcon({ status }: CampaignStatusProps) {
  const theme = useTheme();
  const category = getTestStatusCategory(status);
  let props: Pick<IconCommonV2Props, 'color' | 'icon'>;
  switch (category) {
    case TestStatusCategory.accepted:
      props = {
        color: theme.dec.color.primary.default.fg.positive,
        icon: 'checkOutlined',
      };
      break;
    case TestStatusCategory.waiting:
      props = {
        color: theme.dec.color.primary.default.fg.minimal,
        icon: 'schedule',
      };
      break;
    case TestStatusCategory.rejected:
      props = {
        color: theme.dec.color.primary.default.fg.negative,
        icon: 'closeOutlined',
      };
      break;
    case TestStatusCategory.forced:
      props = {
        color: theme.dec.color.primary.default.fg.moderate,
        icon: status === TestStatusForced.forceAccepted ? 'visibilityShow' : 'visibilityHide',
      };
      break;
  }
  return <IconV2 {...props} size={16} />;
}
